import { p, tp } from '../../i18n/useP';
import { cmpVal, personIsActive, buildSearchKey } from '../../utils/personTable';
/* Modèle « effectif mensuel » par personne — partagé entre :
   - Effectifs (grille mensuelle détaillée par personne)
   - Vue d'ensemble / Synthèse (FTE moyen annuel du cockpit)
   Une seule source pour que les deux écrans ne divergent jamais. */

export const MONTHS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
export const ALL_M = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

export function parseLocalDate(s) {
  if (!s) return null;
  const [y, m, d] = String(s).slice(0, 10).split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

// Date entrée/sortie affichée en mois-année (mm-aaaa).
export function fmtMonthYear(s) {
  if (!s) return '—';
  const d = parseLocalDate(s);
  if (!d) return '—';
  return `${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
}

export const fmtChfInt = (n) => Math.round(Number(n) || 0).toLocaleString('de-CH').replace(/\./g, "'");

function contractActiveInMonth(c, year, monthIdx) {
  const monthStart = new Date(year, monthIdx, 1);
  const monthEnd   = new Date(year, monthIdx + 1, 0);
  if (!c.start_date) return false;
  const start = parseLocalDate(c.start_date);
  if (start > monthEnd) return false;
  if (c.end_date) {
    const end = parseLocalDate(c.end_date);
    if (end < monthStart) return false;
  }
  return true;
}

// Contrat de référence pour les colonnes fixes (Salaire 100%, EPT contrat).
// Préfère un contrat ouvert, sinon le plus récent par start_date.
function pickReferenceContract(contracts) {
  if (!contracts || contracts.length === 0) return null;
  const open = contracts.filter(c => c.end_date == null);
  const pool = open.length > 0 ? open : contracts;
  return pool.reduce(
    (best, c) => (!best || (c.start_date || '') > (best.start_date || '') ? c : best),
    null,
  );
}

// Contrat actif un mois donné pour calculer la cellule mensuelle.
function pickContractForMonth(contracts, year, monthIdx) {
  const active = contracts.filter(c => contractActiveInMonth(c, year, monthIdx));
  if (active.length === 0) return null;
  const open = active.filter(c => c.end_date == null);
  const pool = open.length > 0 ? open : active;
  return pool.reduce(
    (best, c) => (!best || (c.start_date || '') > (best.start_date || '') ? c : best),
    null,
  );
}

// kind: 'before' (avant entry_date), 'after' (après termination_date),
//       'none' (dans la fenêtre people mais sans contrat couvrant le mois),
//       'active' (taux contrat actif * 100)
function buildPersonMonthlyCells(person, contracts, year) {
  const entry = person.entry_date ? parseLocalDate(person.entry_date) : null;
  const term  = person.termination_date ? parseLocalDate(person.termination_date) : null;
  return ALL_M.map(m => {
    const monthStart = new Date(year, m, 1);
    const monthEnd   = new Date(year, m + 1, 0);
    if (entry && entry > monthEnd)  return { kind: 'before', pct: 0 };
    if (term  && term  < monthStart) return { kind: 'after',  pct: 0 };
    const c = pickContractForMonth(contracts, year, m);
    if (!c) return { kind: 'none', pct: 0 };
    return { kind: 'active', pct: Math.round(Number(c.activity_rate || 0) * 100) };
  });
}

// Métadonnées colonnes triables (les 12 mois ne sont pas triables).
// La colonne personne porte `personSort` : tri via pastilles P/N
// (first_name_sort / last_name_sort), pas par clic sur l'en-tête.
export const SORT_COLUMNS = [
  { key: 'person',           label: tp('phrases:p_984f5046d6'),  align: 'left',  personSort: true         },
  { key: 'department',       label: tp('phrases:p_3d7c87c2af'),   align: 'left',  detail: true             },
  { key: 'salary100',        label: p('100% (CHF/m)'),  align: 'right', detail: true, sub: p('Salaire brut') },
  { key: 'entry_date',       label: tp('phrases:p_74ec72cc90'), align: 'left',  detail: true             },
  { key: 'termination_date', label: p('Date de sortie'), align: 'left', detail: true             },
];

// Comparateur partagé (nulls last, numeric-first) — source unique utils/personTable.
export { cmpVal };

// Vue par personne sur l'année : 1 ligne = 1 personne, cellules mensuelles.
// Bornes externes = people.entry_date / termination_date ; taux mensuel = contrat
// actif ce mois-là. Tri par défaut : actifs d'abord, puis entry_date desc.
export function buildPeopleView(people, contracts, year) {
  return buildPeopleViewSpan(people, contracts, [year]);
}

/* Même vue sur PLUSIEURS années consécutives : les cellules mensuelles sont
   concaténées année après année (12 × years.length), et la fenêtre de présence
   couvre toute la période. Sert la bascule 1 an / 2 ans des Effectifs : sur deux
   ans, les fins de contrat de l'année suivante deviennent visibles. */
export function buildPeopleViewSpan(people, contracts, years) {
  const yearStart = new Date(years[0], 0, 1);
  const yearEnd   = new Date(years[years.length - 1], 11, 31);
  const byPerson = new Map();
  for (const c of contracts) {
    if (!c.person_id) continue;
    if (!byPerson.has(c.person_id)) byPerson.set(c.person_id, []);
    byPerson.get(c.person_id).push(c);
  }
  return people
    .filter(p => {
      if (p.entry_date && parseLocalDate(p.entry_date) > yearEnd) return false;
      if (p.termination_date && parseLocalDate(p.termination_date) < yearStart) return false;
      return true;
    })
    .map(p => {
      const pcontracts = byPerson.get(p.id) || [];
      const ref = pickReferenceContract(pcontracts);
      const ar = ref ? Number(ref.activity_rate || 0) : 0;
      const ms = ref ? Number(ref.monthly_salary || 0) : 0;
      return {
        ...p,
        department: ref?.department || '',
        activityPct: ref ? Math.round(ar * 100) : null,
        salary100: (ref && ar > 0) ? ms / ar : 0,
        cells: years.flatMap(y => buildPersonMonthlyCells(p, pcontracts, y)),
        // Tri prénom / nom séparés (pastilles P/N) + filtre standard.
        first_name_sort: (p.first_name || '').toLowerCase(),
        last_name_sort:  (p.last_name  || '').toLowerCase(),
        is_active: personIsActive(pcontracts),
        _search: buildSearchKey(p.first_name, p.last_name, ref?.department),
      };
    })
    .sort((a, b) => {
      const today = new Date();
      const aActive = !a.termination_date || parseLocalDate(a.termination_date) >= today;
      const bActive = !b.termination_date || parseLocalDate(b.termination_date) >= today;
      if (aActive !== bActive) return aActive ? -1 : 1;
      return (b.entry_date || '').localeCompare(a.entry_date || '');
    });
}

/* Totaux mensuels (FTE = Σ taux actifs ; headcount = nb taux > 0) + moyennes.
   La longueur suit celle des cellules (12 mois, ou 24 sur une vue à deux ans) :
   la moyenne porte donc sur la période affichée, pas sur une année figée.
   `months` force la longueur quand la liste est vide (aucune personne filtrée). */
export function monthlyTotals(rows, months = 12) {
  const n = rows.length > 0 ? rows[0].cells.length : months;
  const idx = Array.from({ length: n }, (_, i) => i);
  const monthlyFte = idx.map(i =>
    rows.reduce((acc, p) => {
      const cell = p.cells[i];
      return cell.kind === 'active' ? acc + cell.pct / 100 : acc;
    }, 0),
  );
  const monthlyHeadcount = idx.map(i =>
    rows.reduce((acc, p) => {
      const cell = p.cells[i];
      return acc + (cell.kind === 'active' && cell.pct > 0 ? 1 : 0);
    }, 0),
  );
  return {
    monthlyFte,
    monthlyHeadcount,
    avgFte: monthlyFte.reduce((a, v) => a + v, 0) / n,
    avgHeadcount: monthlyHeadcount.reduce((a, v) => a + v, 0) / n,
  };
}
